import { OrderHistory } from '../stores/appOrderHistoryStore';
import { RevenueHistory } from '../services/revenuehistory/revenueHistoryService';
import { PriceAlert } from '../services/priceAlerts.ts/priceAlertsService';

/**
 * New filtering approach using explicit start and end dates.
 * Simpler than the old cursor + view approach.
 */

/**
 * Check if a date falls within the given date range (inclusive)
 */
export function isDateInRange(date: Date | string, startDate: Date, endDate: Date): boolean {
  const d = date instanceof Date ? date : new Date(date);

  // Normalize all dates to midnight for comparison
  const normalized = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const normalizedStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const normalizedEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

  return normalized >= normalizedStart && normalized <= normalizedEnd;
}

/**
 * Filter orders by date range
 */
export function filterOrdersByDateRange(
  orders: OrderHistory[],
  startDate: Date,
  endDate: Date
): OrderHistory[] {

  const filteredOrders = orders
    .filter(order => isDateInRange(order.orderDate, startDate, endDate))
    .sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());


  return filteredOrders;
}

/**
 * Filter revenue by date range
 */
export function filterRevenueByDateRange(
  revenue: RevenueHistory[],
  startDate: Date,
  endDate: Date
): RevenueHistory[] {
  return revenue
    .filter(rev => isDateInRange(rev.date, startDate, endDate))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/**
 * Get the date range from all orders (first to last)
 */
export function getOrdersDateRange(orders: OrderHistory[]): { start: Date; end: Date } | null {
  if (orders.length === 0) return null;

  const dates = orders.map(o => new Date(o.orderDate));
  const start = new Date(Math.min(...dates.map(d => d.getTime())));
  const end = new Date(Math.max(...dates.map(d => d.getTime())));

  return { start, end };
}

/**
 * Get the date range from all revenue records (first to last)
 */
export function getRevenueDateRange(revenue: RevenueHistory[]): { start: Date; end: Date } | null {
  if (revenue.length === 0) return null;

  const dates = revenue.map(r => new Date(r.date));
  const start = new Date(Math.min(...dates.map(d => d.getTime())));
  const end = new Date(Math.max(...dates.map(d => d.getTime())));

  return { start, end };
}

/**
 * Get the combined date range from both orders and revenue
 * Useful for initializing 'all' time selection with actual data range
 */
export function getCombinedDateRange(
  orders: OrderHistory[],
  revenue: RevenueHistory[]
): { start: Date; end: Date } | null {
  const orderRange = getOrdersDateRange(orders);
  const revenueRange = getRevenueDateRange(revenue);

  if (!orderRange && !revenueRange) return null;
  if (!orderRange) return revenueRange;
  if (!revenueRange) return orderRange;

  const start = new Date(Math.min(orderRange.start.getTime(), revenueRange.start.getTime()));
  const end = new Date(Math.max(orderRange.end.getTime(), revenueRange.end.getTime()));

  return { start, end };
}

/**
 * Filter priceAlerts by date range
 */
export function filterPriceAlertsByDateRange(
  priceAlerts: PriceAlert[],
  startDate: Date,
  endDate: Date
): PriceAlert[] {
  return priceAlerts
    .filter(alert => isDateInRange(alert.toDate, startDate, endDate))
    .sort((a, b) => new Date(b.toDate).getTime() - new Date(a.toDate).getTime());
}
